import { createContext } from 'react';
import { useContext } from 'react';

import { Functor } from 'functions/Functor';
import { fireShoppingCartAlert } from 'functions/fireAlert';
import EntityType from 'types/ShoppingCart/EntityType';
import * as ShoppingCart from 'types/ShoppingCart/ShoppingCart';
import * as ShoppingCartContext from 'types/ShoppingCart/ShoppingCartContext';
import * as ShoppingCartList from 'types/ShoppingCart/ShoppingCartList';
import STATUS from 'types/ShoppingCart/Status';

export const ShoppingCartsContext = createContext<{
	value: ShoppingCartContext.Type;
	setValue: (input: ShoppingCartContext.Type) => void;
	initializeWithoutDetails: () => Promise<void>;
}>({
	value: ShoppingCartContext.create(),
	setValue: (_input: ShoppingCartContext.Type) => {},
	initializeWithoutDetails: async () => {},
});

// TODO: Try to use a Factory probably
export function useShoppingCartsContext() {
	const { value, setValue } = useContext(ShoppingCartsContext);

	function setName(id: string, name: string): void {
		setValue({
			...value,
			carts: ShoppingCartList.setName(value.carts, id, name),
		});
	}

	function addNewCart() {
		setValue({
			...value,
			carts: ShoppingCartList.addNewCart(value.carts),
		});
	}

	function setDescription(id: string, description: string): void {
		setValue({
			...value,
			carts: ShoppingCartList.setDescription(value.carts, id, description),
		});
	}

	async function saveCart(id: string): Promise<void> {
		setValue({
			...value,
			carts: await ShoppingCartList.save(value.carts, id),
		});
	}

	async function setQuantity(
		id: string,
		input: {
			itemId: string;
			quantity: number;
		}
	): Promise<void> {
		setValue({
			...value,
			carts: await ShoppingCartList.setQuantity(value.carts, id, input),
		});
	}

	function setProductQuantity(
		id: string,
		input: {
			productId: string;
			quantity: number;
		}
	): void {
		setValue({
			...value,
			carts: ShoppingCartList.setProductQuantity(value.carts, id, input),
		});
	}

	async function remove(id: string, name: string): Promise<void> {
		setValue({
			...value,
			carts: await ShoppingCartList.deleteFromApi(value.carts, id, name),
		});
	}

	function getAll(): ShoppingCartList.Type {
		return value.carts.filter((e) => e.id !== 'new');
	}

	function getAllLive(): ShoppingCartList.Type {
		return Functor.From(value.carts)
			.map((carts) => carts.filter((e) => e.status === STATUS.LIVE))
			.map((carts) => ShoppingCartList.orderByDefault(carts))
			.get();
	}

	async function initializeSingleCart(id: string) {
		const updatedCarts = await ShoppingCartList.initializeSingleCart(value.carts, id);
		setValue({
			...value,
			carts: updatedCarts,
		});
		return updatedCarts;
	}

	async function enrichWithProductInformations(id: string): Promise<void> {
		setValue({
			...value,
			carts: await ShoppingCartList.enrichWithProductInformations(value.carts, id),
		});
	}

	function getByStatus(status: STATUS): ShoppingCartList.Type {
		return value.carts.filter((e) => e.status === status && e.id !== '');
	}

	function getById(id: string): ShoppingCart.Type | undefined {
		return value.carts.find((e) => e.id === id);
	}

	async function removeAppearance(cartId: string, appearanceId: string): Promise<void> {
		setValue({
			...value,
			carts: await ShoppingCartList.removeAppearance(value.carts, cartId, appearanceId),
		});
	}

	function getDefault(): ShoppingCart.Type | undefined {
		return ShoppingCartList.getDefault(value.carts);
	}

	async function setDefault(id: string): Promise<void> {
		setValue({
			...value,
			carts: await ShoppingCartList.setDefault(value.carts, id),
		});
	}

	async function moveToNextStatus(id: string, previousDefaultName: string): Promise<void> {
		setValue({
			...value,
			carts: await ShoppingCartList.moveToNextStatus(value.carts, id, previousDefaultName),
		});
	}

	async function moveToPrevStatus(id: string): Promise<void> {
		setValue({
			...value,
			carts: await ShoppingCartList.moveToPrevStatus(value.carts, id),
		});
	}

	async function addEntityToCart(
		entity: {
			id: string;
			type: EntityType;
			amount: number;
			basketIds?: string[];
		},
		shoppingCartId: string | undefined
	): Promise<void> {
		setValue({
			...value,
			modalId: null,
			carts: await ShoppingCartList.addEntityToCart(value.carts, entity, shoppingCartId),
		});
		fireShoppingCartAlert();
	}

	function setModalId(id: string | null): void {
		setValue({ ...value, modalId: id });
	}

	function openModal(entityId: string | null, entityType: EntityType): void {
		setValue({ ...value, modalId: entityId, entityType: entityType });
	}

	return {
		addEntityToCart: addEntityToCart,
		addNewCart: addNewCart,
		enrichWithProductInformations: enrichWithProductInformations,
		get: getById,
		getAll: getAll,
		getAllLive: getAllLive,
		getByStatus: getByStatus,
		getDefault: getDefault,
		initializeSingleCart: initializeSingleCart,
		moveToNextStatus: moveToNextStatus,
		moveToPrevStatus: moveToPrevStatus,
		remove: remove,
		removeAppearance: removeAppearance,
		saveCart: saveCart,
		setDefault: setDefault,
		setDescription: setDescription,
		setName: setName,
		setProductQuantity: setProductQuantity,
		setQuantity: setQuantity,
		setModalId: setModalId,
		modalId: value.modalId,
		entityType: value.entityType,
		openModal: openModal,
	};
}
